/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 28.01.25
 *
 */
import {asArray, uuidv4} from "@/utils/helpers";
import dayjs from "dayjs";
import {AgeDateDisplayEnum} from "@shared/Genogram/AgeDateDisplayEnum";
import {
	GenogramConnectionCategoryEnum,
	getGenogramConnectionCategoryByLineSubType,
} from "@shared/Genogram/GenogramConnectionCategoryEnum";
import {
	isStickyNoteNodeData,
	syncStickyNoteNodeData
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramStickyNoteHelper";
import {isInstitutionNodeData, INSTITUTION_DATE_FORMAT_DEFAULT} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramInstitutionHelper";


export const GenogramEnum = Object.freeze({
	CREATE_PARTNER: "create_partner",
	SIBLINGS: "siblings",
	KIDS: "kids",
})



export function createMaleNode(options = {}) {
	return createNode({
		...options,
		type: "male",
	})
}

export function createFemaleNode(options = {}) {
	return createNode({
		...options,
		type: "female",
	})
}

// Function to create a single node
export function createNode({id, type, position, data = {}}) {
	return {
		data: {
			...data,
			id: id || uuidv4(),
			type: type || data.type,
		},
		group: 'nodes',
		position: data.position || position,
	};
}

export function createChildEdge(options = {}) {
	let {lineSubtype, lineType, ...optionsRest} = options
	return createEdge({
		...optionsRest,
		lineType: lineType || "bezier",
		lineSubType: lineSubtype || "straight",
		connectionCategory: GenogramConnectionCategoryEnum.CHILD,
	})
}
export function createMarriageEdge(options = {}) {
	let {lineSubType, ...optionsRest} = options
	return createEdge({
		...optionsRest,
		lineType: "segments",
		lineSubType: lineSubType || "family-marriage",
		connectionCategory: GenogramConnectionCategoryEnum.SOCIAL,
	})
}

// Function to create an edge between two nodes
export function createEdge({sourceId, targetId, edgeId, id, lineSubType = "", lineType="", connectionCategory = null, ...dataRest}) {
	return {
		data: {
			...dataRest,
			connectionCategory: connectionCategory || getGenogramConnectionCategoryByLineSubType(lineSubType),
			lineType: lineType || "bezier",
			lineSubType,
			source: sourceId,
			target: targetId,
			id: id || edgeId || uuidv4(),
		},
		group: 'edges',
	};
}

export const createAuxNode = ({parentEdge, childNode, x, xPositionPercent}) => {
	const auxNodeId = 'aux_' + uuidv4();

	let auxNodesParent = getData(parentEdge).auxNodes || [];
	auxNodesParent.push(auxNodeId)
	setData(parentEdge, {auxNodes: auxNodesParent})

	let auxNodesChild = getData(childNode).auxNodes || [];
	auxNodesChild.push(auxNodeId)
	setData(childNode, {auxNodes: auxNodesChild})


	return {
		data: {
			isAuxNode: true,
			parentEdgeId: getData(parentEdge).id || null,
			childNodeId: getData(childNode).id || null,
			// xPositionPercent: xPositionPercent,
			type: 'round-rectangle',
			name: '',
			resize: true,
			width: 10,
			height: 10,
			id: auxNodeId,
			skipSnapToGridY: true,
		},
		position: {
			x: x,
			// y: y1,
		},
	};
}

export function prepareCopyAuxNodes(nodes, _oldIdToNewId) {
	let nodesLocal = asArray(nodes)
	console.log("prepareCopyAuxNodes: nodesLocal = ", nodesLocal)
	nodesLocal.forEach(node => {
		// if (!node || !getData(node, 'isAuxNode')) return;
		const data = getData(node)
		console.log("prepareCopyAuxNodes: data = ", data)
		if (data.parentEdgeId && _oldIdToNewId[data.parentEdgeId]) {
			data.parentEdgeId = _oldIdToNewId[data.parentEdgeId];
		}
		if (data.childNodeId && _oldIdToNewId[data.childNodeId]) {
			data.childNodeId = _oldIdToNewId[data.childNodeId];
		}
		if(Array.isArray(data.auxNodes)){
			data.auxNodes = data.auxNodes.map(auxNodeId => _oldIdToNewId[auxNodeId])
		}
	});
	return nodes;
}

export function nonempty(node){
	if(!node){
		return false
	}
	if(typeof node.data === "function") {
		return node.nonempty()
	}
	return true
}

export function getPosition(node){
	if(!node){
		return
	}
	if(typeof node.position === "function") {
		return node.position()
	}
	return node.position
}
export function setPosition(node, position){
	if(typeof node.position === "function") {
		node.position(position)
	} else {
		node.position = node.position || {}
		if(position.x != null){
			node.position.x = position.x
		}
		if(position.y != null){
			node.position.y = position.y
		}
	}
}
export function getData(node, key){
	if(typeof node.data === "function") {
		return node.data(key)
	}
	return key ? node.data[key] : node.data
}

export function setData(node, data){
	if(typeof node.data === "function") {
		node.data(data)
	} else {
		node.data = node.data || {}
		node.data = {
			...node.data,
			...data
		}
	}
}


function asDataFn(elem){
	let data = elem.data
	if(typeof data === "object"){
		data = (params) => {
			if(params){
				elem.data = {
					...elem.data,
					...params
				}
			} else {
				return elem.data
			}
		}
	} else {
		data = (args) => elem.data(args)
	}
	return data
}

export function syncNodeLabel(nodeData, options = {}) {
	if(isStickyNoteNodeData(nodeData)){
		syncStickyNoteNodeData(nodeData)
		return
	}
	if(nodeData.isHandwritingLabelMoved){
		nodeData.label = ""
		return
	}
	if(isInstitutionNodeData(nodeData)){
		syncInstitutionLabel(nodeData)
		return
	}
	options.ageDateDisplay = options.ageDateDisplay || AgeDateDisplayEnum.FULL
	let age = null
	let dateOfBirth = null
	let dateOfDeath = null
	if (nodeData.dateOfBirth) {
		let rightDate = dayjs()
		if (nodeData.isPassedAway && nodeData.dateOfDeath) {
			rightDate = dayjs(nodeData.dateOfDeath)
		}
		age = rightDate.diff(dayjs(nodeData.dateOfBirth), 'year')
	}
	let bornAndDied = nodeData.isPassedAway && nodeData.dateOfDeath && nodeData.dateOfBirth
	if (nodeData.dateOfBirth) {
		let text = "Born: "
		if (bornAndDied) {
			text = ""
		}
		dateOfBirth = text + dayjs(nodeData.dateOfBirth).format(nodeData.birthFormat || "MMM DD, YYYY")
	}
	if (nodeData.isPassedAway && nodeData.dateOfDeath) {
		let text = ""
		if(nodeData.dateOfBirth && age && age <= 0){
			text = "Stillbirth or miscarriage: "
		} else {
			text = "Passed away: "
		}
		if (bornAndDied) {
			text = ""
		}
		dateOfDeath = text + dayjs(nodeData.dateOfDeath).format(nodeData.deathFormat || "MMM DD, YYYY")
	}
	if (bornAndDied) {
		dateOfBirth = dateOfBirth + " - " + dateOfDeath
		dateOfDeath = null
	}
	if(age !== null){
		if (age === 0) {
			age = 'Infant'
		} else {
			age = age + " y.o."
		}
	}
	switch (options.ageDateDisplay) {
		case AgeDateDisplayEnum.AGE_ONLY:
			dateOfBirth = null
			break
		case AgeDateDisplayEnum.DATE_ONLY:
			age = null
			break
		case AgeDateDisplayEnum.FULL:
			break
	}
	let canShowPublicNote = nodeData.notePublic && !nodeData.hideNote
	let notePublic = canShowPublicNote ? "\n" + nodeData.notePublic : null
	let textLine = [nodeData.relationship, nodeData.gender, age].filter(item => !!item).join(", ")
	nodeData.label = [nodeData.name, textLine, dateOfBirth, dateOfDeath, notePublic].filter(item => !!item).join(",\n")
}

function syncInstitutionLabel(nodeData) {
	let startText = formatInstitutionDate(nodeData.startDate, nodeData.startFormat)
	let endText = (nodeData.endDate || nodeData.isEndDateEnabled) ? formatInstitutionDate(nodeData.endDate, nodeData.endFormat) : null
	let dateLine = startText && endText ? `${startText} - ${endText}` : startText || endText || null
	let noteText = nodeData.notePublic || null
	nodeData.label = [nodeData.name, dateLine, noteText].filter(item => !!item).join(",\n")
}

function formatInstitutionDate(dateValue, format = INSTITUTION_DATE_FORMAT_DEFAULT) {
	if(!dateValue){
		return ""
	}
	return dayjs(dateValue).format(format || INSTITUTION_DATE_FORMAT_DEFAULT)
}
